import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';
import { buildEntityUrl, encodeSlug } from 'utils/seo';
import formatDirectionName from 'helpers/formatDirectionName';

import SchoolIcon from 'assets/icons/famicons_school.svg?react';
import FiltersIcon from 'assets/icons/filters.svg?react';
import ShieldIcon from 'assets/icons/iconamoon_shield-yes-fill.svg?react';
import MailIcon from 'assets/icons/mail.svg?react';
import PhoneIcon from 'assets/icons/phone.svg?react';
import PinIcon from 'assets/icons/location.svg?react';
import Footer from 'components/Footer';
import Header from 'components/Header';
import HeadlineBackground from 'components/HeadlineBackground';
import Helmet from 'components/Helmet';
import { ResultsWrapper } from 'components/ResultsLayout';
import LinearProgress from 'components/LinearProgress';
import PageContent from 'components/PageContent';
import SocialMedia from 'pages/Course/SocialMedia';
import coursesEndPoint from 'endPoints/courses';
import MainLayout from 'layouts/MainLayout';
import useDataListFactory from 'services/dataList';
import useInfiniteScroll from 'hooks/useInfiniteScroll';
import { getPlatformById } from 'actions/platforms';
import { getFilterOptions } from 'actions/educations';
import getCurrentId from 'helpers/getCurrentId';
import CourseCard from './../Courses/components/CourseCard';
import EducationListFilters from 'components/EducationListFilters';
import SortIcon from 'assets/icons/iconoir_sort.svg?react';
const Breadcrumbs = React.lazy(() => import('components/BreadCrumbs'));

const useDataList = (props, options) => useDataListFactory(props, options);

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 48,
  fontWeight: 500,
  lineHeight: '40px',
  color: theme.palette.textPrimary.main,
  marginBottom: 0,
  [theme.breakpoints.down('sm')]: {
    fontSize: 32,
    lineHeight: '32px'
  }
}));

const Content = styled(PageContent)(({ pt }) => ({
  paddingBottom: 56,
  paddingTop: pt || 40,
  position: 'relative',
  zIndex: 2
}));

const HeadlineBg = HeadlineBackground;

const Card = styled('div')(({ p }) => ({
  backgroundColor: '#fff',
  borderRadius: 18,
  border: '1px solid rgba(10, 10, 10, 1)',
  boxShadow: '4px 4px 0px 0px rgba(10, 10, 10, 1)',
  padding: p || 24,
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1
}));

const CardGrey = styled('div')(() => ({
  backgroundColor: 'rgba(238, 237, 242, 1)',
  borderRadius: 18,
  padding: 24,
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1
}));

const List = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(2, 1fr)`,
  gap: 24,
  marginBottom: 8,
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: `repeat(1, 1fr)`,
    gap: 16
  }
}));

const CourseDescription = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'mb' && prop !== 'noMargin'
})(({ mb, noMargin, theme }) => {
  let marginBottomValue = 5;
  if (noMargin) {
    marginBottomValue = 0;
  } else if (mb !== undefined && mb !== null) {
    // Convert Material-UI spacing units (8px per unit) to pixels
    marginBottomValue = typeof mb === 'number' ? mb * 8 : parseInt(mb) || 5;
  }
  
  return {
    fontSize: 15,
    marginBottom: `${marginBottomValue}px`,
    '&.MuiTypography-root': {
      marginBottom: `${marginBottomValue}px`
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: noMargin ? '0px' : (mb !== undefined && mb !== null ? `${(typeof mb === 'number' ? mb * 8 : parseInt(mb) || 20)}px` : '20px'),
      '&.MuiTypography-root': {
        marginBottom: noMargin ? '0px' : (mb !== undefined && mb !== null ? `${(typeof mb === 'number' ? mb * 8 : parseInt(mb) || 20)}px` : '20px')
      }
    }
  };
});

const Flex = styled('div')(({ gap, theme }) => ({
  display: 'flex',
  gap: gap || 85,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: 16
  }
}));

const FlexItem = styled('div')(({ flex }) => ({
  flex: flex || 1
}));

const TextIcon = styled(Typography)(({ mb }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 16,
  lineHeight: '24px',
  marginBottom: mb || 0,
  color: 'rgba(10, 10, 10, 1)',
  '& svg': {
    width: 24
  },
  '& span': {
    flex: 4
  }
}));

const TagsButton = styled(Button)(() => ({
  cursor: 'pointer',
  border: '1px solid rgba(10, 10, 10, 1)',
  padding: '7px 17px',
  borderRadius: 8
}));

const ShowMoreLink = styled(Button)(({ theme }) => ({
  border: 'none',
  outline: 'none',
  boxShadow: 'none',
  textDecoration: 'underline',
  textTransform: 'none',
  padding: '4px 8px',
  minWidth: 'auto',
  fontSize: 14,
  color: theme.palette.primary.main || '#1976d2',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
    backgroundColor: 'transparent',
    boxShadow: 'none'
  },
  '&:focus': {
    outline: 'none',
    boxShadow: 'none'
  }
}));

const TextIconFlex = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 24,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: 16,
    alignItems: 'flex-start'
  }
}));

const ResultsLabel = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  paddingLeft: 3,
  color: theme.palette.textDarkGray.main
}));

const ResultsLabelContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8
}));

const Filters = styled('div')(() => ({
  borderRadius: 18,
  backgroundColor: 'rgba(238, 237, 242, 1)',
  padding: 24,
  paddingTop: 16,
  paddingBottom: 16
}));

const FiltersTitle = styled(Typography)(({ theme }) => ({
  fontSize: 20,
  fontWeight: 500,
  marginBottom: 12,
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: 16
  },
  [theme.breakpoints.down('sm')]: {
    cursor: 'pointer',
    marginBottom: 0
  }
}));

const Wrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 24,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column-reverse'
  }
}));

const LeftSidebar = styled('div')(() => ({
  marginBottom: 60,
  flex: 2
}));

const RightSidebar = styled('div')(() => ({
  flex: 1,
  position: 'relative'
}));

const BreadcrumbsWrapper = styled('div')(() => ({
  marginBottom: 24
}));

const TitleRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  marginBottom: '32px !important'
}));

const PlatformLogo = styled('img')(() => ({
  height: 47,
  objectFit: 'contain',
  maxWidth: 200,
  minWidth: 75
}));

const SentinelSpacer = styled('div')(() => ({
  height: 1
}));

const IconButtonStyled = styled(IconButton)(() => ({
  padding: 0,
  margin: 3
}));

const LinkStyled = styled('div')(({ mb }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: '#fff',
  color: 'rgba(10, 10, 10, 1)',
  borderRadius: 8,
  padding: 16,
  marginBottom: mb || 16,
  cursor: 'pointer'
}));

const PlatformPage = () => {
  const helmet = useTranslate('HELMET');
  const navigate = useNavigate();
  const t = useTranslate('Platform');
  const tReviews = useTranslate('Reviews');
  const tDirections = useTranslate('Directions');
  const tPlatforms = useTranslate('Platforms');
  const dispatch = useDispatch();
  const location = useLocation();
  const [currentId, setCurrentId] = React.useState(getCurrentId(location));
  const isMobile = useSelector((state) => state.app.isMobile);
  const [filtersView, setFiltersView] = React.useState(true);
  const [platformInfo, setPlatformInfo] = React.useState(null);
  const [filterOptions, setFilterOptions] = React.useState([]);
  const [showAllDirections, setShowAllDirections] = React.useState(false);
  const [isLoadingPlatform, setIsLoadingPlatform] = React.useState(true);

  const { actions, data, loading, page, perPage, count, filters, sort } = useDataList(
    coursesEndPoint,
    {
      filters: {
        platforms: currentId || undefined
      }
    }
  );
  const limitReached = React.useMemo(() => count <= page * perPage, [count, page, perPage]);

  const handleChangePage = React.useCallback(async () => {
    actions.onChangePage(page, true, true);
  }, [actions, page]);

  React.useEffect(() => {
    const newId = getCurrentId(location);
    setCurrentId((prev) => {
      if (newId !== prev) {
        setPlatformInfo(null); // Reset platform info when navigating to a new platform
        return newId;
      }
      return prev;
    });
  }, [location]);

  React.useEffect(() => {
    if (!currentId) return;
    setIsLoadingPlatform(true);
    setPlatformInfo(null); // Show loading state
    dispatch(getPlatformById(currentId)).then((res) => {
      setIsLoadingPlatform(false);
      if (res && typeof res === 'object') {
        if (Object.keys(res).length === 0) {
          navigate('/404', { replace: true });
          return;
        }
        setPlatformInfo(res);
      } else {
        setPlatformInfo(null);
        navigate('/404', { replace: true });
      }
    });
  }, [dispatch, currentId, navigate]);

  React.useEffect(() => {
    if (!currentId) return;
    
    dispatch(getFilterOptions({ platforms: [currentId] })).then((res) => {
      if (res && typeof res === 'object') setFilterOptions(res);
      else setFilterOptions({});
    });
  }, [dispatch, currentId]);

  const handleToggleFiltersView = React.useCallback(() => {
    if (!isMobile) return;
    setFiltersView((prev) => !prev);
  }, [isMobile]);

  const handleChangeString = React.useCallback(
    (e, name) => {
      actions.onFilterChange(
        {
          ...filters,
          [name]: e.target.value
        },
        false
      );
    },
    [actions, filters]
  );

  const handleChangeFilter = React.useCallback(
    (name, value) => {
      if (typeof value === 'boolean') {
        actions.onFilterChange(
          {
            ...filters,
            [name]: value
          },
          true
        );
        return;
      }

      const source = (filters[name] || '').split(',') || [];

      const result = source.includes(value)
        ? source.filter((degree) => degree !== value)
        : [...source, value];

      actions.onFilterChange(
        {
          ...filters,
          [name]: result.filter(Boolean).join(',')
        },
        true
      );
    },
    [actions, filters]
  );

  const handleClearFilters = React.useCallback(() => {
    actions.onFilterChange({ platforms: currentId || undefined });
  }, [actions, currentId]);

  const isDurationChecked = React.useCallback(
    (value) => {
      const [min, max] = value;
      const key = `${min}, ${Number.isFinite(max) ? max : ''}`;
      const current = filters.duration;
      if (Array.isArray(current)) {
        return current.includes(key);
      }
      if (typeof current === 'string') {
        return current === key;
      }
      return false;
    },
    [filters]
  );

  const handleChangeDuration = React.useCallback(
    (value) => {
      const [min, max] = value;
      const key = `${min}, ${Number.isFinite(max) ? max : ''}`;
      const current = filters.duration;

      const next = current === key ? undefined : key;

      actions.onFilterChange(
        {
          ...filters,
          duration: next
        },
        true
      );
    },
    [actions, filters]
  );

  const { sentinelRef } = useInfiniteScroll({
    isLoading: loading,
    hasMore: !limitReached,
    onLoadMore: handleChangePage,
    rootMargin: '200px 0px',
    threshold: 0
  });

  const [sortAnchorEl, setSortAnchorEl] = React.useState(null);
  const sortMenuOpen = Boolean(sortAnchorEl);

  const handleSort = React.useCallback(
    (column, direction) => {
      if (sort[column] && sort[column] === direction) {
        actions.onSortChange(column, null);
        setSortAnchorEl(null);
        return;
      }
      actions.onSortChange(column, direction);
      setSortAnchorEl(null);
    },
    [actions, sort]
  );

  const sortValues = React.useMemo(
    () => [
      { column: 'courseName', direction: 'asc', name: t('ByNameAsc') },
      { column: 'courseName', direction: 'desc', name: t('ByNameDesc') }
    ],
    [t]
  );

  const directionTags = React.useMemo(
    () =>
      (platformInfo?.directions || []).map((topic) => {
        const selected = (filters.directions || '').split(',').includes(topic);
        return (
          <TagsButton
            key={topic}
            variant={selected ? 'contained' : 'outlined'}
            color={selected ? 'primary' : 'inherit'}
            onClick={() => handleChangeFilter('directions', topic)}>
            {formatDirectionName(topic, tDirections)}
          </TagsButton>
        );
      }),
    [platformInfo?.directions, filters.directions, handleChangeFilter, tDirections]
  );

  // Calculate if we need to show "Show more" button
  // Desktop: ~6 tags per row, 2 rows = ~12 tags
  // Mobile: show all (no limit on mobile)
  const maxRows = 2;
  const estimatedTagsPerRow = 6;
  const maxVisibleTags = maxRows * estimatedTagsPerRow;
  const hasMoreDirections = !isMobile && directionTags.length > maxVisibleTags && !showAllDirections;
  
  // Reset showAllDirections when directions change
  React.useEffect(() => {
    setShowAllDirections(false);
  }, [platformInfo?.directions]);

  const displayedDirectionTags = React.useMemo(() => {
    if (showAllDirections || isMobile || directionTags.length <= maxVisibleTags) {
      return directionTags;
    }
    return directionTags.slice(0, maxVisibleTags);
  }, [directionTags, showAllDirections, isMobile, maxVisibleTags]);

  const canonicalUrl = React.useMemo(() => {
    if (!platformInfo) return undefined;
    const slug = platformInfo.slug || encodeSlug(platformInfo.name);
    return buildEntityUrl('platform', slug, platformInfo._id);
  }, [platformInfo]);

  const breadcrumbs = React.useMemo(() => {
    const items = [
      { link: '/', label: 'Home' },
      { link: '/education', label: 'Education' },
      { link: '/education/platforms', label: tPlatforms('Title'), raw: true }
    ];
    
    if (platformInfo?.name) {
      items.push({
        link: location.pathname,
        label: platformInfo.name,
        raw: true
      });
    }
    
    return items;
  }, [platformInfo, location.pathname, tPlatforms]);

  return (
    <>
      <Helmet
        meta={{
          title: helmet('platformTitle') + (platformInfo?.name ? ` - ${platformInfo.name}` : ''),
          description: platformInfo?.description || helmet('platformDescription'),
          ogTitle: helmet('platformOgTitle') + (platformInfo?.name ? ` - ${platformInfo.name}` : ''),
          ogDescription: platformInfo?.description || helmet('platformOgDescription')
        }}
        canonicalUrl={canonicalUrl}
      />
      <MainLayout>
        <Header />

        <HeadlineBg />

        <Content style={{ paddingBottom: 20 }}>
          <React.Suspense fallback={null}>
            <BreadcrumbsWrapper>
              <Breadcrumbs breadcrumbs={breadcrumbs} />
            </BreadcrumbsWrapper>
          </React.Suspense>
          <Card>
            <Flex>
              <FlexItem flex={2}>
                <TextIconFlex>
                  <TextIcon>
                    <SchoolIcon />
                    {t('OnlinePlatform')}
                  </TextIcon>
                  <TextIcon>
                    <ShieldIcon />
                    {t('VerifiedInstitution')}
                  </TextIcon>
                </TextIconFlex>

                <TitleRow>
                  {isLoadingPlatform ? (
                    <Skeleton variant="text" width={300} height={48} />
                  ) : (
                    <Title variant="h1">{platformInfo?.name || 'Platform'}</Title>
                  )}
                </TitleRow>

                {isLoadingPlatform ? (
                  <>
                    <Skeleton variant="text" width="100%" height={24} />
                    <Skeleton variant="text" width="80%" height={24} />
                  </>
                ) : (
                  <Box sx={{ mb: 4 }}>
                    <CourseDescription>{platformInfo?.description || '-'}</CourseDescription>
                  </Box>
                )}

                {isLoadingPlatform ? (
                  <Stack
                    direction="row"
                    sx={{
                      flexWrap: "wrap",
                      gap: '8px',
                      mb: 3
                    }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} variant="rounded" width={100} height={36} />
                    ))}
                  </Stack>
                ) : (
                  <>
                    <Stack
                      direction="row"
                      sx={{
                        flexWrap: "wrap",
                        gap: '8px',
                        mb: 0
                      }}>
                      {displayedDirectionTags}
                    </Stack>
                    {(hasMoreDirections || (!isMobile && showAllDirections && directionTags.length > maxVisibleTags)) && (
                      <Stack
                        direction="row"
                        sx={{
                          justifyContent: "center",
                          width: '100%',
                          mt: 2,
                          mb: 0
                        }}>
                        {hasMoreDirections ? (
                          <ShowMoreLink onClick={() => setShowAllDirections(true)}>
                            {tReviews('ShowMore')}
                          </ShowMoreLink>
                        ) : (
                          <ShowMoreLink onClick={() => setShowAllDirections(false)}>
                            {tReviews('ShowLess')}
                          </ShowMoreLink>
                        )}
                      </Stack>
                    )}
                  </>
                )}
              </FlexItem>

              <FlexItem>
                <CardGrey>
                  <LinkStyled>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {isLoadingPlatform ? (
                        <Skeleton variant="rounded" width={150} height={47} />
                      ) : platformInfo?.logo ? (
                        <PlatformLogo
                          src={`${platformInfo.logo}/public`}
                          alt={platformInfo?.name || 'Platform logo'}
                        />
                      ) : null}
                    </div>
                  </LinkStyled>

                  {!isLoadingPlatform && <SocialMedia list={platformInfo?.socialMedia} />}
                  {platformInfo?.contactInfo?.[0]?.city && (
                    <TextIcon mb={16}>
                      <PinIcon />
                      <span>{platformInfo.contactInfo[0].city}</span>
                    </TextIcon>
                  )}
                  {Array.isArray(platformInfo?.contactInfo?.[0]?.phone) &&
                    platformInfo.contactInfo[0].phone.length > 0 && (
                      <TextIcon mb={16}>
                        <PhoneIcon />
                        <span>{platformInfo.contactInfo[0].phone.join(', ')}</span>
                      </TextIcon>
                    )}
                  {Array.isArray(platformInfo?.contactInfo?.[0]?.email) &&
                    platformInfo.contactInfo[0].email.length > 0 && (
                      <TextIcon mb={16}>
                        <MailIcon />
                        <span>{platformInfo.contactInfo[0].email.join(', ')}</span>
                      </TextIcon>
                    )}
                </CardGrey>
              </FlexItem>
            </Flex>
          </Card>
        </Content>

        <Content style={{ paddingTop: 0 }}>
          <Wrapper>
            <LeftSidebar>
              <ResultsWrapper>
                {data && data.length === 0 ? (
                  <ResultsLabel>{t('NoData')}</ResultsLabel>
                ) : (
                  <>
                    <ResultsLabelContainer>
                      <ResultsLabel>{t('FoundResults', { count })}</ResultsLabel>
                      {loading && <CircularProgress size={16} />}
                    </ResultsLabelContainer>
                    <IconButtonStyled
                      aria-label={t('Sort')}
                      onClick={(e) => setSortAnchorEl(e.currentTarget)}>
                      <SortIcon />
                    </IconButtonStyled>
                    <Menu
                      anchorEl={sortAnchorEl}
                      open={sortMenuOpen}
                      onClose={() => setSortAnchorEl(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                      {sortValues.map((item) => (
                        <MenuItem
                          key={`${item.column}-${item.direction}`}
                          selected={sort[item.column] === item.direction}
                          onClick={() => handleSort(item.column, item.direction)}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
              </ResultsWrapper>

              <List>
                {(data || []).map((item, idx) => (
                  <CourseCard key={idx} item={item} />
                ))}
              </List>

              {loading || !data ? <LinearProgress /> : null}
              {limitReached ? null : (
                <SentinelSpacer ref={sentinelRef} aria-label={t('LoadMore')} />
              )}
            </LeftSidebar>
            <RightSidebar>
              <Filters>
                <FiltersTitle onClick={handleToggleFiltersView}>
                  <FiltersIcon />
                  {t('filtersTitle')}
                </FiltersTitle>

                {filtersView ? (
                  <EducationListFilters
                    filters={filters}
                    filterOptions={filterOptions}
                    onChangeFilter={handleChangeFilter}
                    onChangeString={handleChangeString}
                    onClear={handleClearFilters}
                    isDurationChecked={isDurationChecked}
                    onChangeDuration={handleChangeDuration}
                  />
                ) : null}
              </Filters>
            </RightSidebar>
          </Wrapper>
        </Content>

        <Footer />
      </MainLayout>
    </>
  );
};

export default PlatformPage;
